/** @format */

import { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import FadeLoader from "react-spinners/FadeLoader";

import Footer from "../components/Footer";
import Headers from "../components/Headers";
import NosPartenaires from "../components/NosPartenaires";
import { messageClear } from "../store/reducers/homeReducer";
import { useCategories } from "../utils/Categories";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const LoadingOverlay = () => (
  <div
    role='status'
    aria-busy='true'
    aria-label='Chargement en cours'
    className='fixed inset-0 z-[99999] flex items-center justify-center bg-gray-500/70'
  >
    <FadeLoader />
  </div>
);

const AboutSection = ({
  title,
  paragraphs = [],
  imageSrc,
  imageAlt = "",
  imageLeft = false,
  bgImage,
}) => {
  const imageBlock = (
    <div
      className='w-1/2 sm:w-full flex justify-center items-center px-[2.5%] opacity-60'
      style={
        bgImage
          ? { backgroundImage: `url('${bgImage}')`, backgroundSize: "cover" }
          : undefined
      }
    >
      <img src={imageSrc} alt={imageAlt} className='max-w-full h-auto' />
    </div>
  );

  const textBlock = (
    <div className='w-1/2 sm:w-full px-[2.5%]'>
      {title && (
        <h2 className='font-bold text-blue-400 text-3xl my-5'>{title}</h2>
      )}
      {paragraphs.map((para, index) =>
        typeof para === "string" ? (
          <p key={index} className='text-justify text-xl mb-3 text-gray-600'>
            {para}
          </p>
        ) : (
          para
        ),
      )}
    </div>
  );

  return (
    <section className='bg-slate-50 shadow-sm flex sm:flex-col py-5 sm:px-2 sm:gap-4 mb-5'>
      {imageLeft ? (
        <>
          {imageBlock}
          {textBlock}
        </>
      ) : (
        <>
          {textBlock}
          {imageBlock}
        </>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Custom hook — gestion des notifications d'authentification
// ---------------------------------------------------------------------------

const useAuthNotifications = () => {
  const dispatch = useDispatch();
  const { loader, successMessage, messageError } = useSelector(
    (state) => state.auth,
  );

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (messageError) {
      toast.error(messageError);
      dispatch(messageClear());
    }
  }, [successMessage, messageError, dispatch]);

  return { loader };
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const About = () => {
  const { t } = useTranslation();
  const { loader } = useAuthNotifications();
  const { categories, clientLocation } = useCategories();

  return (
    <div>
      {loader && <LoadingOverlay />}

      <Headers categories={categories} clientLocation={clientLocation} />

      <header className='text-center mt-8 mb-6'>
        <h1 className='text-3xl font-bold text-blue-400'>{t("About Us")}</h1>
      </header>

      {/* Section 1 — A propos */}
      <AboutSection
        title={t("A propos de gobymall.com")}
        paragraphs={[t("About_kimeyada_com")]}
        imageSrc='/images/2025.png'
        imageAlt='Illustration gobymall 2025'
        bgImage='/images/9195240.png'
      />

      {/* Section 2 — Mission & Valeurs */}
      <AboutSection
        imageLeft
        imageSrc='/images/pc.png'
        imageAlt='Illustration mission gobymall'
        paragraphs={[
          <h2
            key='mission-title'
            className='font-bold text-blue-400 text-3xl my-5'
          >
            {t("Our Mission")}
          </h2>,
          t("our_mission_p1"),
          t("our_mission_p2"),
          <h2
            key='values-title'
            className='font-bold text-blue-400 text-3xl my-5'
          >
            {t("valeurs apportés")}
          </h2>,
          t("GOBYMALL ne promet pas :"),
          t("GOBYMALL promet : "),
        ]}
      />

      {/* Section 3 — Anytime, Anywhere */}
      <AboutSection
        title={t("Anytime, Anywhere")}
        paragraphs={[t("anytime_anywhere_p1")]}
        imageSrc='/images/crossshipping.png'
        imageAlt='Livraison partout dans le monde'
        bgImage='/images/9195240.png'
      />

      <NosPartenaires />
      <Footer />
    </div>
  );
};

export default About;
